import { useState, useMemo } from "react";
import CardItem from "../../models/Carditem"
import { TableRow, TableCell, Stack, Box, IconButton, Collapse, Typography } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useRestaurantData } from "../../provider/RestaurantDataContext";
import { calcPrice } from "./CartPage";
import { useCardContent } from "../../provider/CardContentContext";
import ItemOptionsDialog, { SelectedItemOptions } from "../dialogs/itemOptionsDialog/ItemOptionsDialog";
import { currencyFormat } from "../../utils/Utils";

interface CartRowProps {
    item: CardItem;
    index: number;
}

export default function CartRow({ item, index }: CartRowProps) {
    const { t } = useTranslation();
    const { restaurantData, restaurantData: { currency } } = useRestaurantData();
    const { addItemToCart, removeItemFromCart, updateItem } = useCardContent();

    const [open, setOpen] = useState(false);
    const [stateChange, setStateChange] = useState<number>(0);
    const [itemOptionsDialogOpen, setItemOptionsDialogOpen] = useState(false);

    const hasDetailPage = useMemo(() => {
        return item.selectedOptions.itemExtras.filter(itemExtra => itemExtra.amount > 0).length > 0 || item.selectedOptions.itemOptionGroups.length > 0;
    }, [item]);

    const newCardItem = useMemo((): CardItem => {
        return new CardItem(item.quantity, item.selectedOptions, item.id, item.price, item.allergens, item.foodTypes, item.imageUrl, item.symbolicImage, item.disabled, item.itemType, item.itemExtras, item.itemOptionGroups, item.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, stateChange]);

    const decreaseQuantity = (item: CardItem) => {
        removeItemFromCart(item);
    }

    const increaseQuantity = (item: CardItem) => {
        addItemToCart(item, item.selectedOptions);
    }

    const openItemOptionsDialog = () => {
        setItemOptionsDialogOpen(true);
    }

    const handleItemOptionDialogConfirmation = (selectedOptions: SelectedItemOptions) => {
        updateItem(item, selectedOptions);
    }

    return (<>
        <TableRow key={item.id}>
            <TableCell>
                <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() => setOpen(!open)}
                >
                    {index + 1}{hasDetailPage ? open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon /> : <></>}
                </IconButton>
            </TableCell>
            <TableCell>{item.value.value}</TableCell>
            <TableCell align="center">
                <Stack direction={'column'}>
                    <Box>{item.quantity}</Box>
                    <Stack direction={'row'} justifyContent={'space-around'}>
                        <IconButton sx={{ p: 0 }} onClick={() => increaseQuantity(item)}>
                            <AddIcon />
                        </IconButton>
                        <IconButton sx={{ p: 0 }} onClick={() => decreaseQuantity(item)}>
                            {item.quantity > 1 ? <RemoveIcon /> : <DeleteIcon />}
                        </IconButton>
                    </Stack>
                </Stack>
            </TableCell>
            <TableCell align="right">{`${currencyFormat(calcPrice(item))}${restaurantData.currency.detail}`}</TableCell>
        </TableRow>
        {
            hasDetailPage &&
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" gutterBottom component="div">
                                    {t("orderDetails")}
                                </Typography>
                                <IconButton sx={{ p: 0 }} onClick={() => openItemOptionsDialog()}>
                                    <EditIcon />
                                </IconButton>
                            </Box>
                            {
                                item.selectedOptions.itemOptionGroups.map((optionGroup) => {
                                    return (
                                        <Typography key={optionGroup.id}>{`${optionGroup.value.value}: ${optionGroup.itemOptions[0].value.value} ${optionGroup.itemOptions[0].price > 0 ? currencyFormat(optionGroup.itemOptions[0].price) + currency.detail : ''}`}</Typography>
                                    )
                                })
                            }
                            {
                                item.selectedOptions.itemExtras.filter(itemExtra => itemExtra.amount > 0).map((itemExtra) => {
                                    return (
                                        <Typography key={itemExtra.id}>{`${itemExtra.value.value} x ${itemExtra.amount} ${itemExtra.price > 0 ? (itemExtra.price * itemExtra.amount) + currency.detail : ''}`}</Typography>
                                    )
                                })
                            }
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        }
        {
            itemOptionsDialogOpen &&
            <ItemOptionsDialog
                item={newCardItem}
                opened={itemOptionsDialogOpen}
                onConfirmation={handleItemOptionDialogConfirmation}
                close={() => { setItemOptionsDialogOpen(false); setStateChange(Math.random()); }} />
        }
    </>
    )
}